import { ScrollStrategy } from '@angular/cdk/overlay';
import { ComponentFactoryResolver, ViewContainerRef } from '@angular/core';
import { StylesService } from '@df/core/service/styles';

/** Valid ARIA roles for a modal element. */
export type ModalRole = 'dialog' | 'alertdialog';

export type TModalPosition = 'top' | 'right' | 'bottom' | 'left' | 'custom';

/** Possible overrides for a modal's position. */
export interface ModalPosition {
    /** Override for the modal's top position. */
    top?: string;

    /** Override for the modal's bottom position. */
    bottom?: string;

    /** Override for the modal's left position. */
    left?: string;

    /** Override for the modal's right position. */
    right?: string;
}

/**
 * Configuration for opening a modal modal with the MatModal service.
 */
export class ModalConfig<D = any> {
    /**
     * Where the attached component should live in Angular's *logical* component tree.
     * This affects what is available for injection and the change detection order for the
     * component instantiated inside of the modal. This does not affect where the modal
     * content will be rendered.
     */
    viewContainerRef?: ViewContainerRef;

    /** ID for the modal. If omitted, a unique one will be generated. */
    id?: string;

    /** The ARIA role of the modal element. */
    role?: ModalRole = 'dialog';

    /** Custom class for the overlay panel. */
    panelClass?: string | string[] = '';

    /** Custom class for the modal. */
    modalClass?: string | string[] = '';

    /** Whether the modal has a backdrop. */
    hasBackdrop?: boolean = true;

    /** Custom class for the backdrop, */
    backdropClass?: string = '';

    /** Whether the user can use escape or clicking on the backdrop to close the modal. */
    disableClose?: boolean = false;

    /** Width of the modal. */
    width?: string = '';

    /** Height of the modal. */
    height?: string = '';

    /** Min-width of the modal. If a number is provided, pixel units are assumed. */
    minWidth?: number | string;

    /** Min-height of the modal. If a number is provided, pixel units are assumed. */
    minHeight?: number | string;

    /** Max-width of the modal. If a number is provided, pixel units are assumed. Defaults to 80vw */
    maxWidth?: number | string = StylesService.vw(80);

    /** Max-height of the modal. If a number is provided, pixel units are assumed. */
    maxHeight?: number | string;

    /** Custom transform options */
    transformOptions?: string = 'scale(0.7)';
    transitionOptions?: string = '150ms cubic-bezier(0, 0, 0.2, 1)';

    /** Position overrides. */
    position?: ModalPosition;

    /** Data being injected into the child component. */
    data?: D | null = null;

    /** ID of the element that describes the modal. */
    ariaDescribedBy?: string | null = null;

    /** ID of the element that labels the modal. */
    ariaLabelledBy?: string | null = null;

    /** Aria label to assign to the modal element */
    ariaLabel?: string | null = null;

    /** Whether the modal should focus the first focusable element on open. */
    autoFocus?: boolean = false;

    /**
     * Whether the modal should capture focus when
     * a focusable element outside of modal is clicked
     */
    trapFocus?: boolean;

    /**
     * Whether the modal should restore focus to the
     * previously-focused element, after it's closed.
     */
    restoreFocus?: boolean = true;

    /** Scroll strategy to be used for the modal. */
    scrollStrategy?: ScrollStrategy | string;

    /**
     * If modal should close when path is changed
     */
    closeOnNavigation?: boolean = true;

    /**
     * If modal should close when location is changed (router event start)
     */
    closeOnRouterStart?: boolean = false;

    /** Alternate `ComponentFactoryResolver` to use when resolving the associated component. */
    componentFactoryResolver?: ComponentFactoryResolver;

    /** On close callback */
    onClose?: () => void;

    /** On open callback */
    onOpen?: () => void;

    /** On backdrop click callback */
    onBackdropClick?: () => void;
}
